import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DashboardLayout from "@/components/DashboardLayout";
import {
  changePassword,
  changePasswordUnauthenticated,
  requestChangePasswordPin,
  requestChangePasswordPinUnauthenticated,
} from "@/api/benutzer";

const passwordRules = [
  { label: "Mindestens 8 Zeichen", test: (p: string) => p.length >= 8 },
  { label: "Mindestens ein Kleinbuchstabe", test: (p: string) => /[a-z]/.test(p) },
  { label: "Mindestens ein Großbuchstabe", test: (p: string) => /[A-Z]/.test(p) },
  { label: "Mindestens eine Zahl", test: (p: string) => /[0-9]/.test(p) },
  { label: "Mindestens ein Sonderzeichen", test: (p: string) => /[^a-zA-Z0-9]/.test(p) },
];

const ChangePassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const usernameFromUrl = searchParams.get("username") ?? "";
  const isUnauthenticated = usernameFromUrl !== "";

  const [username, setUsername] = useState(usernameFromUrl);
  const [oldPassword, setOldPassword] = useState("");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pin, setPin] = useState("");
  const [step, setStep] = useState<"form" | "pin">("form");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleRequestPin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("Die neuen Passwörter stimmen nicht überein.");
      return;
    }

    const failedRule = passwordRules.find((r) => !r.test(newPassword));
    if (failedRule) {
      setError(`Passwort erfüllt nicht alle Anforderungen: ${failedRule.label}.`);
      return;
    }

    setLoading(true);
    try {
      const result = isUnauthenticated
        ? await requestChangePasswordPinUnauthenticated({ Username: username })
        : await requestChangePasswordPin();
      if (result.isSuccess) {
        setStep("pin");
      } else {
        setError(result.message ?? "PIN konnte nicht angefordert werden.");
      }
    } catch (err: any) {
      setError(err?.message ?? "Fehler beim Anfordern des PINs.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    setLoading(true);
    try {
      const result = isUnauthenticated
        ? await changePasswordUnauthenticated({ Username: username, Email: email, NewPassword: newPassword, Pin: pin })
        : await changePassword({ OldPassword: oldPassword, NewPassword: newPassword, Pin: pin });
      if (result.isSuccess) {
        if (isUnauthenticated) {
          navigate("/login?message=Passwort+wurde+erfolgreich+ge%C3%A4ndert.");
        } else {
          setSuccess(result.message);
          setOldPassword("");
          setEmail("");
          setNewPassword("");
          setConfirmPassword("");
          setPin("");
          setStep("form");
        }
      } else {
        setError(result.message ?? "Passwort konnte nicht geändert werden.");
      }
    } catch (err: any) {
      setError(err?.message ?? "Fehler beim Ändern des Passworts.");
    } finally {
      setLoading(false);
    }
  };

  const formContent = (
    <Paper elevation={isUnauthenticated ? 3 : 2} sx={{ p: 4 }}>
      {step === "form" ? (
        <form onSubmit={handleRequestPin}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {isUnauthenticated && (
              <Box sx={{ mb: 1, textAlign: "center" }}>
                <Typography variant="h5" component="h1" fontWeight="bold" gutterBottom>
                  Passwort ändern
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Bitte geben Sie Ihren Benutzernamen und Ihre E-Mail-Adresse ein
                </Typography>
              </Box>
            )}

            {isUnauthenticated && (
              <TextField
                label="Benutzername"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                fullWidth
                autoComplete="username"
              />
            )}

            {success && <Alert severity="success">{success}</Alert>}

            {isUnauthenticated ? (
              <TextField
                label="E-Mail-Adresse"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                autoComplete="email"
              />
            ) : (
              <TextField
                label="Aktuelles Passwort"
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                required
                fullWidth
                autoComplete="current-password"
              />
            )}

            <TextField
              label="Neues Passwort"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              fullWidth
              autoComplete="new-password"
            />

            {newPassword.length > 0 && (
              <Box sx={{ px: 1, py: 0.5, bgcolor: "action.hover", borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                  Passwort-Anforderungen
                </Typography>
                <List dense disablePadding>
                  {passwordRules.map((rule) => {
                    const met = rule.test(newPassword);
                    return (
                      <ListItem key={rule.label} disableGutters sx={{ py: 0.25, px: 1 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          {met ? (
                            <CheckCircleIcon fontSize="small" color="success" />
                          ) : (
                            <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={rule.label}
                          primaryTypographyProps={{ variant: "caption", color: met ? "success.main" : "text.secondary" }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            )}

            <TextField
              label="Neues Passwort bestätigen"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              fullWidth
              autoComplete="new-password"
              error={confirmPassword.length > 0 && newPassword !== confirmPassword}
              helperText={
                confirmPassword.length > 0 && newPassword !== confirmPassword
                  ? "Passwörter stimmen nicht überein"
                  : undefined
              }
            />

            {error && <Alert severity="error">{error}</Alert>}

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading || (isUnauthenticated && !username) || (isUnauthenticated ? !email : !oldPassword) || !newPassword || !confirmPassword}
                sx={{ color: "white", flex: 1 }}
              >
                {loading ? "Wird gesendet..." : "Passwort ändern"}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate(isUnauthenticated ? "/login" : -1 as any)}
                disabled={loading}
              >
                Abbrechen
              </Button>
            </Box>
          </Box>
        </form>
      ) : (
        <form onSubmit={handleVerifyAndChange}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Zwei-Faktor-Authentifizierung
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bitte geben Sie den 6-stelligen PIN ein, den Sie per E-Mail erhalten haben
              </Typography>
            </Box>

            <TextField
              label="PIN-Code"
              type="text"
              placeholder="123456"
              inputProps={{ maxLength: 6 }}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              required
              fullWidth
              sx={{
                "& input": {
                  textAlign: "center",
                  fontSize: "2rem",
                  letterSpacing: "0.5rem",
                },
              }}
            />

            {error && <Alert severity="error">{error}</Alert>}

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={pin.length !== 6 || loading}
              sx={{ color: "white" }}
            >
              {loading ? "Wird geändert..." : "Verifizieren & Passwort ändern"}
            </Button>

            <Button
              variant="text"
              size="large"
              fullWidth
              onClick={() => {
                setStep("form");
                setPin("");
                setError(null);
              }}
              disabled={loading}
            >
              Zurück
            </Button>
          </Box>
        </form>
      )}
    </Paper>
  );

  if (isUnauthenticated) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
          p: 2,
        }}
      >
        <Container maxWidth="sm">{formContent}</Container>
      </Box>
    );
  }

  return (
    <DashboardLayout>
      <Box sx={{ maxWidth: 480, mx: "auto" }}>
        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
          Passwort ändern
        </Typography>
        {formContent}
      </Box>
    </DashboardLayout>
  );
};

export default ChangePassword;
